import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import PropTypes from "prop-types";
import { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { NavLink, useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { getCartCountSelector } from "../../../store/selectors/cartItems.selector";
import { getChangePasswordRequired, getHeaderToken, getHeaderNavData, isAuthenticated, getTTSecondOrderStatus, getMTUserStatus, getUserType, getSoldToNumber } from "../../../store/selectors/navItems.selector";
import { clearHomePageDataAction, setTTSecondOrderStatusAction, setSoldToNumberAction } from "../../../store/actions/navItems.actions";
import { ttSecondOrderStatusAction, ttGetSecondOrderStatusAction } from "../../../store/actions/orderHistory.actions";
import { getSelectedAssociatedCustomer, getSelectedAssociatedCustomerSapId } from "../helper/helper";
import { LocalContext } from "../../../LocalProvider";
import { colors } from "../../../styles/variables";
import SingleColumn from "../../styled/SingleColumn.styled";
import NavList from "../header/NavList";

const renderHtml = (value) => {
  if (typeof value === "string" && /<[a-z][\s\S]*>/i.test(value)) {
    return <span dangerouslySetInnerHTML={{ __html: value }} />;
  }
  return value;
};

const NewHeader = ({ appHeaderAPI }) => {
  const { translate } = useContext(LocalContext);
  const [open, setOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, nextValue: null });
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const dispatch = useDispatch();
  const history = useHistory();

  const navDataFromRedux = useSelector(getHeaderNavData);
  const isAuth = useSelector(isAuthenticated);
  const data = navDataFromRedux || appHeaderAPI;
  const currentLanguage = localStorage.getItem("language") || "en";
  const totalCountCartItems = useSelector(getCartCountSelector);
  const changePasswordRequired = useSelector(getChangePasswordRequired);
  const token = useSelector(getHeaderToken);
  const mtUser = useSelector(getMTUserStatus);
  const userType = useSelector(getUserType);
  const ttSecondOrderStatus = useSelector(getTTSecondOrderStatus);
  const soldToNumber = useSelector(getSoldToNumber);

  const getSoldToId = () => userType === "Profile" ? (getSelectedAssociatedCustomerSapId() || getSelectedAssociatedCustomer()) : getSelectedAssociatedCustomer();
  const secondOrderEnabled = Number(ttSecondOrderStatus) === 1;

  // ── BUSINESS_HOLD_START ───────────────────────────────────────────────────
  // TT 2nd Order Status feature is temporarily disabled on business request.
  // The API calls below are commented out to prevent unnecessary network requests
  // while the banner/UI is hidden. Re-enable together with the UI block below.
  // To re-enable: remove the block comment markers around fetchTTStatus and
  // both useEffect hooks, and restore the UI block marked BUSINESS_HOLD_START
  // further down in this file.

  // const fetchTTStatus = (id) => {
  //   dispatch(ttGetSecondOrderStatusAction(isAuth, token, id))
  //     .then((res) => {
  //       if (res) {
  //         const status = res.ttSecondOrderStatus !== undefined
  //           ? res.ttSecondOrderStatus
  //           : res.TTSecondOrderStatus;
  //         if (status !== undefined) {
  //           dispatch(setTTSecondOrderStatusAction(status));
  //         }
  //       }
  //     })
  //     .catch(() => {});
  // };

  // Non-profile users (TT, BCM, BNC, etc.):
  // Fires on page refresh and login when isAuth becomes true.
  // customerID is resolved from the auth token server-side, no soldToId needed.
  // useEffect(() => {
  //   if (!isAuth) {
  //     dispatch(setSoldToNumberAction(null));
  //     setBannerOpen(false);
  //     return;
  //   }
  //   if (!token || mtUser === true || userType === "Profile") return;
  //   fetchTTStatus(null);
  // }, [isAuth, token, userType, mtUser]);

  // Profile users only:
  // Fires when soldToNumber changes in Redux (customer switch via NavList).
  // Also fires on page refresh/login once soldToNumber is resolved from localStorage.
  // useEffect(() => {
  //   if (!isAuth || !token || mtUser === true || userType !== "Profile") return;
  //   const id = soldToNumber || getSoldToId();
  //   if (!id) return;
  //   if (id !== soldToNumber) {
  //     dispatch(setSoldToNumberAction(id));
  //   }
  //   fetchTTStatus(id);
  // }, [soldToNumber, isAuth, token, userType, mtUser]);
  // ── BUSINESS_HOLD_END ─────────────────────────────────────────────────────

  const handleLogoClick = (e) => {
    if (changePasswordRequired && token) {
      e.preventDefault();
      dispatch(clearHomePageDataAction(token, true, changePasswordRequired)).then(() => {
        history.push("/home");
      }).catch((error) => {
        console.error('Logout failed:', error);
        history.push("/home");
      });
    } else {
      history.push("/home");
    }
  };

  const handleToggleClick = (e) => {
    e.preventDefault();
    const nextValue = !secondOrderEnabled;
    setConfirmDialog({ show: true, nextValue });
    setApiError("");
  };

  const handleConfirm = () => {
    const soldToNumber = getSoldToId();
    const status = confirmDialog.nextValue ? 1 : 0;
    setApiLoading(true);
    setApiError("");
    dispatch(ttSecondOrderStatusAction(isAuth, token, { soldToNumber, status }))
      .then(() => {
        dispatch(setTTSecondOrderStatusAction(status));
        setConfirmDialog({ show: false, nextValue: null });
        setBannerOpen(false);
      })
      .catch(() => {
        setApiError("Something went wrong. Please try again.");
      })
      .finally(() => {
        setApiLoading(false);
      });
  };

  const handleCancel = () => {
    setConfirmDialog({ show: false, nextValue: null });
    setApiError("");
  };

  if (data) {
    return (
      <HeaderHolder>
        <SingleColumn>
          <Nav>
            <Logo to="/" onClick={handleLogoClick}>
              <img src={data.logo} alt="PepsiCo"></img>
            </Logo>

            <CartIcon to="/cart-preview">
              <ShoppingCartIcon />
              <DivCart>
                <SpanCart>{totalCountCartItems}</SpanCart>
              </DivCart>
            </CartIcon>

            <StyledItem open={open} onClick={() => setOpen(!open)}>
              <div />
              <div />
              <div />
            </StyledItem>

            <NavList key={currentLanguage} isOpen={open} navData={data} />
          </Nav>
        </SingleColumn>

        {/* ── TT 2nd Order Status Banner & Controls ────────────────────────────────
            TEMPORARILY HIDDEN on business request (pending confirmation to re-enable).
            All Redux state, API calls, and handlers below remain fully intact.
            To re-enable: remove the opening "{/* BUSINESS_HOLD_START *\/}" and
            closing "{/* BUSINESS_HOLD_END *\/}" comment wrappers around this block.
        ── */}
        {/* BUSINESS_HOLD_START
        {isAuth && mtUser !== true && (
          <>
            <BannerWrapper open={bannerOpen}>
              <SettingsBanner>
                <BannerInner>
                <SettingsBannerLeft>
                  <StatusDot enabled={secondOrderEnabled} />
                  <SettingsBannerTitle>{renderHtml(translate("2nd Order Creation"))}</SettingsBannerTitle>
                  <SettingsBannerHint>
                    {renderHtml(`${translate("2nd order creation is currently")} ${secondOrderEnabled ? translate("Enable").toLowerCase() : translate("Disable").toLowerCase()} ${translate("for this account")}`)}
                  </SettingsBannerHint>
                </SettingsBannerLeft>
                <SettingsBannerRight>
                  <TogglePillInner
                    onClick={!apiLoading ? handleToggleClick : undefined}
                    disabled={apiLoading}
                  >
                    <ToggleTrack enabled={secondOrderEnabled} disabled={apiLoading}>
                      <input
                        type="checkbox"
                        checked={secondOrderEnabled}
                        onChange={() => {}}
                        disabled={apiLoading}
                      />
                      <span />
                    </ToggleTrack>
                    <ToggleStatusText enabled={secondOrderEnabled}>
                      {renderHtml(secondOrderEnabled ? translate("Enable") : translate("Disable"))}
                    </ToggleStatusText>
                  </TogglePillInner>
                  <CollapseBtn onClick={() => setBannerOpen(false)} title="Hide bar">
                    &#x25B2;
                  </CollapseBtn>
                </SettingsBannerRight>
                </BannerInner>
              </SettingsBanner>
            </BannerWrapper>

            <BannerTabRow open={bannerOpen}>
              <BannerInner>
                <BannerTabAlign>
                  <BannerTab onClick={() => setBannerOpen(true)}>
                    <StatusDot enabled={secondOrderEnabled} small />
                    <span>{renderHtml(translate("2nd Order"))}</span>
                    <StatusChip enabled={secondOrderEnabled}>
                      {renderHtml(secondOrderEnabled ? translate("Enable") : translate("Disable"))}
                    </StatusChip>
                    <TabChevron>&#x25BC;</TabChevron>
                  </BannerTab>
                </BannerTabAlign>
              </BannerInner>
            </BannerTabRow>

            {confirmDialog.show && (
              <ConfirmOverlay>
                <ConfirmDialog>
                  <ConfirmTitle>
                    {renderHtml(`${confirmDialog.nextValue ? translate("Enable") : translate("Disable")} ${translate("2nd Order Creation")}?`)}
                  </ConfirmTitle>
                  <ConfirmMsg>
                    {renderHtml(`${translate("Are you sure you want to")} ${confirmDialog.nextValue ? translate("enable") : translate("disable")} ${translate("2nd order creation for this account?")}`)}
                  </ConfirmMsg>
                  {apiError && <ConfirmError>{renderHtml(translate("Something went wrong"))}</ConfirmError>}
                  <ConfirmActions>
                    <CancelBtn onClick={handleCancel} disabled={apiLoading}>
                      {renderHtml(translate("Cancel"))}
                    </CancelBtn>
                    <ContinueBtn onClick={handleConfirm} disabled={apiLoading}>
                      {renderHtml(translate("Continue"))}
                    </ContinueBtn>
                  </ConfirmActions>
                </ConfirmDialog>
              </ConfirmOverlay>
            )}
          </>
        )}
        BUSINESS_HOLD_END */}
      </HeaderHolder>
    );
  } else {
    return null;
  }
};

NewHeader.propTypes = {
  appHeaderAPI: PropTypes.object.isRequired,
};

export default NewHeader;

const HeaderHolder = styled.div`
  z-index: 1;
  box-shadow: 0px 6px 3px #00000029;
  position: sticky;
  top: 0;
  background-color: #fafafa;

  @media (max-width: 768px) {
    & > * {
      padding: 0.25rem 0 !important;
    }
  }
`;
const Nav = styled.nav`
  width: 100%;
  background: #fafafa;
  position: relative;
  padding: 0;
  display: flex;
  justify-content: space-between;
`;
const Logo = styled(NavLink)`
  padding: 0;
  @media (max-width: 768px) {
    padding: 0;
    margin: 0 auto;
    text-align: center;
  }
  @media (max-width: 360px) {
    margin-left: 1.85rem;
  }
  img {
    width: 150px;
    padding: 0.25rem;
    margin-right: 1rem;
    @media (max-width: 950px) {
      width: 160px;
      margin-right: 0;
    }
    @media (max-width: 768px) {
      width: auto;
      max-width: 60%;
    }
  }
`;

const CartIcon = styled(NavLink)`
  padding-top: 1.5rem;
  display: none;
  &.active {
    font-weight: bold;
  }
  @media (max-width: 768px) {
    position: relative;
    display: block;
    color: ${colors.greyDark};
    padding-right: 0.5rem;
    margin-right: 0.5rem;
  }
`;

const StyledItem = styled.div`
  width: 2rem;
  height: 2rem;
  position: absolute;
  top: 15px;
  left: 20px;
  z-index: 20;
  display: none;
  @media (max-width: 768px) {
    display: flex;
    left: 1rem;
    justify-content: space-around;
    flex-flow: column nowrap;
  }
  div {
    width: 2rem;
    height: 0.25rem;
    background-color: ${({ open }) => (open ? "#ccc" : "#333")};
    border-radius: 10px;
    transform-origin: 1px;
    transition: all 0.3s linear;
    &:nth-child(1) {
      transform: ${({ open }) => (open ? "rotate(45deg)" : "rotate(0)")};
    }
    &:nth-child(2) {
      transform: ${({ open }) => (open ? "translateX(100%)" : "translateX(0)")};
      opacity: ${({ open }) => (open ? 0 : 1)};
    }
    &:nth-child(3) {
      transform: ${({ open }) => (open ? "rotate(-45deg)" : "rotate(0)")};
    }
  }
`;

const DivCart = styled.div`
  border-radius: 50%;
  display: inline-block;
  background: #ff4b4b;
  color: white;
  width: 25px;
  height: 25px;
  vertical-align: top;
  position: absolute;
  top: 3px;
  right: 3px;
  font-size: 0.8rem;
  @media (max-width: 768px) {
    right: 0;
  }
`;

const SpanCart = styled.span`
  line-height: 25px;
  display: block;
  color: white;
  vertical-align: top;
  text-align: center;
  font-size: 0.75rem;
`;

/* ─── 2nd Order Settings Banner ──────────────────────────────────────────── */
const BannerWrapper = styled.div`
  overflow: hidden;
  max-height: ${({ open }) => (open ? "200px" : "0")};
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;
const SettingsBanner = styled.div`
  background: linear-gradient(90deg, #e8f4fb 0%, #f5fafd 100%);
  border-top: 1px solid #cce3f0;
  border-left: 4px solid #0076b3;
  display: flex;
  align-items: center;
  min-height: 46px;
  padding: 6px 0;
  @media (max-width: 768px) {
    min-height: 44px;
  }
`;
const BannerInner = styled.div`
  width: 100%;
  max-width: 95%;
  margin: 0 auto;
  padding: 0 0.5rem;
  display: flex;
  align-items: flex-start;
  @media (min-width: 1921px) {
    max-width: 1920px;
  }
`;
const SettingsBannerLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;
const StatusDot = styled.span`
  width: ${({ small }) => (small ? "7px" : "10px")};
  height: ${({ small }) => (small ? "7px" : "10px")};
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: ${({ small }) => (small ? "4px" : "3px")};
  background: ${({ enabled }) => (enabled ? "#22c55e" : "#ef4444")};
  box-shadow: 0 0 0 3px ${({ enabled }) => (enabled ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)")};
  transition: background 0.3s, box-shadow 0.3s;
`;
const SettingsBannerTitle = styled.span`
  font-weight: 700;
  color: #00537d;
  font-size: 0.83rem;
  letter-spacing: 0.01em;
  white-space: normal;
  word-break: break-word;
  flex-shrink: 0;
  * { margin: 0; padding: 0; }
`;
const SettingsBannerHint = styled.span`
  font-size: 0.73rem;
  color: #6b7280;
  font-weight: 400;
  white-space: normal;
  word-break: break-word;
  min-width: 0;
  * { margin: 0; padding: 0; }
  @media (max-width: 860px) { display: none; }
`;
const SettingsBannerRight = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
  padding-top: 2px;
`;
const TogglePillInner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1.5px solid ${({ disabled }) => (disabled ? "#cbd5e1" : "#0076b3")};
  border-radius: 24px;
  padding: 4px 14px 4px 6px;
  box-shadow: 0 1px 4px rgba(0,118,179,0.12);
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
  transition: box-shadow 0.2s, border-color 0.2s;
  &:hover:not([disabled]) {
    box-shadow: 0 2px 8px rgba(0,118,179,0.22);
  }
`;
const ToggleTrack = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  margin: 0;
  flex-shrink: 0;
  pointer-events: none;
  input { opacity: 0; width: 0; height: 0; }
  span {
    position: absolute;
    inset: 0;
    background: ${({ enabled }) => (enabled ? "#0076b3" : "#cbd5e1")};
    border-radius: 22px;
    transition: background 0.28s cubic-bezier(0.4,0,0.2,1);
    &::before {
      content: "";
      position: absolute;
      width: 16px;
      height: 16px;
      left: ${({ enabled }) => (enabled ? "21px" : "3px")};
      top: 3px;
      background: white;
      border-radius: 50%;
      transition: left 0.28s cubic-bezier(0.4,0,0.2,1);
      box-shadow: 0 1px 4px rgba(0,0,0,0.25);
    }
  }
`;
const ToggleStatusText = styled.span`
  font-size: 0.73rem;
  font-weight: 800;
  color: ${({ enabled }) => (enabled ? "#0076b3" : "#94a3b8")};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: color 0.28s;
  min-width: 52px;
`;
const CollapseBtn = styled.button`
  background: none;
  border: 1px solid #cce3f0;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #0076b3;
  font-size: 0.75rem;
  line-height: 1;
  padding: 0 0 1px 0;
  flex-shrink: 0;
  font-weight: 700;
  letter-spacing: 0;
  transition: background 0.15s, transform 0.2s;
  &:hover { background: #dbeafe; transform: scale(1.1); }
`;
const BannerTabRow = styled.div`
  overflow: hidden;
  max-height: ${({ open }) => (open ? "0" : "40px")};
  opacity: ${({ open }) => (open ? "0" : "1")};
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease;
`;
const BannerTabAlign = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;
const BannerTab = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  border: 1px solid #cce3f0;
  border-top: none;
  border-radius: 0 0 10px 10px;
  padding: 4px 12px 5px 10px;
  cursor: pointer;
  font-size: 0.73rem;
  font-weight: 700;
  color: #00537d;
  box-shadow: 0 3px 8px rgba(0,118,179,0.10);
  transition: background 0.15s, box-shadow 0.2s;
  &:hover {
    background: #e8f4fb;
    box-shadow: 0 5px 12px rgba(0,118,179,0.16);
  }
`;
const StatusChip = styled.span`
  font-size: 0.65rem;
  font-weight: 800;
  padding: 1px 7px;
  border-radius: 10px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: ${({ enabled }) => (enabled ? "rgba(34,197,94,0.13)" : "rgba(239,68,68,0.10)")};
  color: ${({ enabled }) => (enabled ? "#16a34a" : "#dc2626")};
  border: 1px solid ${({ enabled }) => (enabled ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.30)")};
`;
const TabChevron = styled.span`
  font-size: 0.7rem;
  color: #0076b3;
  margin-left: 2px;
  line-height: 1;
  display: flex;
  align-items: center;
  padding-top: 1px;
`;

/* ─── Confirmation Dialog ─────────────────────────────────────────────────── */
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease;
`;
const ConfirmDialog = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 28px 28px 24px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  animation: ${slideUp} 0.22s ease;
`;
const ConfirmTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
`;
const ConfirmMsg = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #475569;
  text-align: center;
  line-height: 1.55;
`;
const ConfirmError = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: #dc2626;
  text-align: center;
`;
const ConfirmActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 6px;
  width: 100%;
`;
const CancelBtn = styled.button`
  flex: 1;
  padding: 9px 0;
  border-radius: 8px;
  border: 1.5px solid #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: background 0.15s;
  &:hover:not(:disabled) { background: #f1f5f9; }
`;
const ContinueBtn = styled.button`
  flex: 1;
  padding: 9px 0;
  border-radius: 8px;
  border: none;
  background: #0076b3;
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  transition: background 0.15s;
  &:hover:not(:disabled) { background: #005f8e; }
`;
